import { Fetcher } from '../../api/Fetcher';
import { buildHashParam } from '../../utils/buildHash';
import { getStaticBaseUrl } from '../../utils/getStaticBaseUrl';

import type { DictionaryType } from './constants';
import type { Dictionary, DictionaryFragment } from './types';

export class DictionaryFetcher<File> {
  private basePath: string;

  constructor(
    dictionaryType: DictionaryType,
    private options: {
      onError: (file: string, locale: string) => void;
      parseFile: (file: File) => DictionaryFragment;
      fallbackLocale?: string;
    }
  ) {
    this.basePath = this.getDictionaryBasePath(dictionaryType);
  }

  getDictionaryBasePath(dictionaryType: DictionaryType): string {
    const { base, directory } = getStaticBaseUrl();

    if (TRANSLATION_PATH === 'storybook') {
      return `https://client.pragmaticplaylive.net/apps/translations/1.0.0/${dictionaryType}`;
    }

    if (TRANSLATION_PATH === 'assets' || window.location.protocol === 'http:') {
      return `${base}${directory}/assets/translations/${dictionaryType}`;
    }

    return `${base}/apps/translations/1.0.0/${dictionaryType}`;
  }

  fetch(locale: string, files: string[]): { fetchingPromise: Promise<Dictionary>; abort: () => void } {
    const signals: AbortController[] = [];

    const fetchFileByLocale = (file: string, locale: string): Promise<File> => {
      const controller = new AbortController();
      signals.push(controller);
      return Fetcher.fetch<File>(`${this.basePath}/${locale}/${file}.json?1`, {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        signal: controller.signal,
        ...buildHashParam,
      });
    };

    const fragmentPromises = files.map(
      (file): Promise<DictionaryFragment> =>
        fetchFileByLocale(file, locale)
          .catch((error) => {
            if (error?.message === '404' && this.options.fallbackLocale) {
              return fetchFileByLocale(file, this.options.fallbackLocale);
            }
            throw error;
          })
          .then((result) => this.options.parseFile(result))
          .catch((error) => {
            this.options.onError(file, locale);
            console.error(`[Dictionary Fetcher] Failed to fetch ${file}.json for language: ${locale}`, error);
            throw error;
          })
    );

    return {
      fetchingPromise: Promise.all(fragmentPromises).then((fragmentArray: DictionaryFragment[]) => {
        return Object.assign.apply<null, DictionaryFragment[], DictionaryFragment>(null, fragmentArray);
      }),
      abort: () => signals.forEach((signal) => signal.abort('Aborted by user')),
    };
  }
}
